import React, { useState, useCallback, useRef, useEffect } from "react";
import { Toast } from "primereact/toast";
import { Button } from 'primereact/button';
import { useSearchParams } from "react-router";
import { ExampleSpeciesGrid } from "./components/ExampleSpeciesGrid";
import { SpeciesSearch } from "./components/SpeciesSearch";
import { SpeciesDetails } from "./components/SpeciesDetails";
import { Species, ExtendedSpecies } from "./types/species";
import { API_BASE_URL } from "./constants";

function App() {
  const toast = useRef<Toast>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);
  const [species, setSpecies] = useState<Species[]>([]);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [loadingSpeciesToShow, setLoadingSpeciesToShow] = useState(false);
  const [speciesToShow, setSpeciesToShow] = useState<ExtendedSpecies | null>(
    null
  );

  const showError = useCallback((summary: string, detail: string) => {
    toast.current?.show({
      severity: "error",
      summary,
      detail,
      life: 5000,
    });
  }, []);

  const handleSearch = () => {
    if (!selectedSpecies) {
      return;
    }

    setSearchParams({ species: selectedSpecies.name });
  };

  const fetchDescription = useCallback(
    async (selectedSpecies: Species) => {
      setLoadingSpeciesToShow(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/dnadescriptions?sci_name=${encodeURIComponent(
            selectedSpecies.name
          )}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        // Fetch MIDI path
        const musicPathResponse = await fetch(
          `${API_BASE_URL}/api/dnamusic?taxon=${selectedSpecies.name}`
        );
        if (!musicPathResponse.ok) {
          throw new Error(
            `Failed to fetch music path: ${musicPathResponse.statusText}`
          );
        }
        const musicPathData = await musicPathResponse.json();

        // Fetch MIDI data
        const midiResponse = await fetch(
          `${API_BASE_URL}/api/sequence-stream/${musicPathData.music_path}`
        );
        if (!midiResponse.ok) {
          throw new Error(
            `Failed to fetch MIDI data: ${midiResponse.statusText}`
          );
        }
        const midiArrayBuffer = await midiResponse.arrayBuffer();
        const midiData = new Uint8Array(midiArrayBuffer);

        setSpeciesToShow({
          ...data.data,
          midiData: new Uint8Array(midiData),
        });
        setSearchParams({ species: selectedSpecies.name });

        // Update the species dropdown with at least this species
        setSpecies((prevSpecies) =>
          prevSpecies.some((s) => s.name === selectedSpecies.name)
            ? prevSpecies
            : [
                ...prevSpecies,
                { name: selectedSpecies.name, code: selectedSpecies.name },
              ]
        );
      } catch (error) {
        console.error("Error fetching species description:", error);
        showError(
          "Error",
          error instanceof Error
            ? error.message
            : "Failed to fetch species description"
        );
        setSpeciesToShow(null);
        // Remove the URL parameter if the species wasn't found
        setSearchParams({});
      } finally {
        setLoadingSpeciesToShow(false);
      }
    },
    [setSearchParams, showError]
  );

  // Load species from URL parameter on initial mount
  useEffect(() => {
    const speciesParam = searchParams.get("species");
    if (!speciesParam) return;

    const speciesFromUrl = { name: speciesParam, code: speciesParam };
    if (speciesParam) {
      setSelectedSpecies(speciesFromUrl);
      fetchDescription(speciesFromUrl);
    }
  }, [fetchDescription, searchParams]);

  const fetchSpecies = useCallback(
    async (speciesName: string) => {
      try {
        if (loadingSpecies || !speciesName) return;
        setLoadingSpecies(true);

        const response = await fetch(
          `${API_BASE_URL}/api/dnasuggest?taxon=species&q=${speciesName}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch species: ${response.statusText}`);
        }

        const data: string[] = await response.json();
        const speciesList = data
          .filter((a) => a)
          .map((item: string) => ({
            name: item,
            code: item,
          }));
        setSpecies(speciesList);
      } catch (error) {
        console.error("Error fetching species:", error);
        showError(
          "Error",
          error instanceof Error ? error.message : "Failed to fetch species"
        );
      } finally {
        setLoadingSpecies(false);
      }
    },
    [loadingSpecies, showError]
  );

  const debounceSearch = useCallback(
    (value: string) => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      searchTimeout.current = setTimeout(() => {
        fetchSpecies(value);
      }, 500);
    },
    [fetchSpecies]
  );

  return (
    <div className="app-background p-3 md:p-5">
      <Toast ref={toast} position="top-right" />

      <div className="flex justify-content-center mb-4">
        <Button 
          label="Home" 
          size="large" 
          icon="pi pi-home" 
          className="home-button"
          onClick={() => window.location.href = 'https://www.sonifyspecies.com/'}
        />
      </div>

      <ExampleSpeciesGrid />

      <SpeciesSearch
        selectedSpecies={selectedSpecies}
        onSpeciesSelect={setSelectedSpecies}
        onSearch={handleSearch}
        species={species}
        onFilter={(filter) => debounceSearch(filter)}
        loadingSpecies={loadingSpecies}
        loadingSpeciesToShow={loadingSpeciesToShow}
      />

      {speciesToShow && <SpeciesDetails species={speciesToShow} />}
    </div>
  );
}

export default App;
